import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { Line } from '@components/patterns/Diff/Line';
import {
  DiffDocument,
  parseDiff,
  sliceDiff,
} from '@components/patterns/Diff/parse';
import { PreviewMode } from '@components/patterns/Diff/PreviewMode';
import { LINE_HEIGHT } from '@components/patterns/Diff/utilities';

import css from './styles.module.css';

export interface DiffProps {
  /** The `.diff` source code */
  src: string;
  /** The maximum number of lines to display */
  lines?: number;
  /** The number of lines to display in preview mode. Default is 0 (disabled) */
  previewLines?: number;
  /** An optional line selection to display */
  selection?: { side: 'from' | 'to'; start: number; end: number };
}

export interface Diff {
  scrollToTop(): void;
}

interface DiffInnerProps {
  diff: DiffDocument;
  lines?: number;
  previewLines?: number;
}

const DiffInner = forwardRef<Diff, DiffInnerProps>(function DiffInner(
  { diff, lines = 15, previewLines = 0 },
  ref
) {
  const parentRef = useRef<HTMLDivElement>(null);
  const getScrollElement = useCallback(() => parentRef.current, []);
  const rowVirtualizer = useVirtualizer({
    count: diff.lines.length,
    getScrollElement,
    estimateSize: () => LINE_HEIGHT,
    overscan: 50,
  });
  const rows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const canPreview = !!previewLines && totalSize > LINE_HEIGHT * previewLines;
  const [previewActive, setPreviewActive] = useState(canPreview);

  const scrollToTop = useCallback(() => {
    parentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handlePreviewActiveChange = useCallback(
    (active: boolean) => {
      setPreviewActive(active);
      if (active) {
        scrollToTop();
      }
    },
    [scrollToTop]
  );

  useImperativeHandle(
    ref,
    useCallback(() => ({ scrollToTop }), [scrollToTop])
  );

  // When diff is updated...
  useLayoutEffect(() => {
    const parent = parentRef.current;
    if (!parent) return;

    // Scroll to top of parent element
    parent.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Remove line number column widths
    parent.style.removeProperty(`--original-line-number-width`);
    parent.style.removeProperty(`--updated-line-number-width`);
    // Re-measure the virtualizer (once all chunks have been rendered)
  }, [diff]);

  // When the virtualizer is updated, re-measure line number column widths
  useLayoutEffect(() => {
    const parent = parentRef.current;

    function updateLineNumberWidth(id: string) {
      const lineNumbers: HTMLElement[] = Array.from(
        parent?.querySelectorAll(`[data-id="line-number-${id}"]`) ?? []
      );
      const lineNumberWidth = Math.max(
        0,
        ...Array.from(lineNumbers).map((el) => el.offsetWidth)
      );
      if (lineNumberWidth) {
        parent?.style.setProperty(
          `--${id}-line-number-width`,
          `${lineNumberWidth}px`
        );
      }
    }

    updateLineNumberWidth('original');
    updateLineNumberWidth('updated');
  }, [rows]);

  return (
    <PreviewMode
      enabled={canPreview}
      lineHeight={LINE_HEIGHT}
      lines={previewLines}
      active={previewActive}
      onActiveChange={handlePreviewActiveChange}
    >
      <div
        ref={parentRef}
        className={css.scrollable}
        style={{ '--max-visible-lines': lines }}
      >
        <pre className={css.pre}>
          <code className={css.code} style={{ '--height': `${totalSize}px` }}>
            <span
              className={css.chunks}
              style={{ '--translate-y': `${rows.at(0)?.start ?? 0}px` }}
            >
              {rows.map((row) => {
                const index = row.index;
                const line = diff.lines[index];

                return (
                  <span key={row.key} className={css.chunk}>
                    <Line line={line} counts={diff.counts} />
                  </span>
                );
              })}
            </span>
          </code>
        </pre>
      </div>
    </PreviewMode>
  );
});

export const Diff = forwardRef<Diff, DiffProps>(function Diff(
  { src, lines, previewLines, selection },
  ref
) {
  const parsed = useMemo(() => parseDiff(src), [src]);
  const selected = useMemo(() => {
    if (!selection) return parsed;
    return sliceDiff(parsed, selection) ?? parsed;
  }, [parsed, selection]);

  return (
    <DiffInner
      ref={ref}
      diff={selected}
      lines={lines}
      previewLines={previewLines}
    />
  );
});

export const DIFF_LINE_HEIGHT = LINE_HEIGHT;
